import { useMemo, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Check, Download, ChevronRight, Twitter, Instagram, Facebook, Link as LinkIcon, ChevronLeft } from "lucide-react";
import Seo from "@/components/Seo";

/**
 * AmbassadorKit — public press-kit page for Justin & Cory.
 * Shows their unique promo link, QR code, and ready-to-post copy blocks.
 * URL: /ambassadors/:slug
 */

const KITS = {
  justin: {
    slug: "justin",
    name: "Justin",
    handle: "@justin",
    promo_code: "FOUNDING2",
    promo_url_path: "/pricing?promo=FOUNDING2",
    tagline: "Founding member of The Farm To You — 100% American-raised meat, dropped direct.",
    color_hex: "#1B3B2B",
    social_captions: {
      short: "I'm one of the founding members of @thefarmtoyou — premium American-raised meat delivered. Use my code FOUNDING2 to lock in your spot before the 500-member cap closes. Link in bio.",
      long: (
        "The best steaks I've ever cooked at home didn't come from a supermarket — they came from @thefarmtoyou. " +
        "It's a members-only drop platform for 100% American-raised meat, sourced directly from family farms and fishermen. " +
        "They're capping founding members at 500 seats, and I've got a link that locks in a founding badge + free shipping for life. " +
        "Use code FOUNDING2 at signup 👇"
      ),
    },
  },
  cory: {
    slug: "cory",
    name: "Cory Sims",
    handle: "@corysims",
    promo_code: "FOUNDING3",
    promo_url_path: "/pricing?promo=FOUNDING3",
    tagline: "Founding partner — Cory's pick of ranchers, fishermen, and chefs.",
    color_hex: "#8B2626",
    social_captions: {
      short: "I'm a founding partner at @thefarmtoyou — a members-only drop platform for premium American-raised meat. Use code FOUNDING3 to lock in your founding seat before the 500-member cap closes.",
      long: (
        "I've been working with @thefarmtoyou to bring premium American-raised meat directly to your door — no supermarkets, no middlemen. " +
        "It's a members-only drop platform capped at 500 founding members, and I'm helping make sure the folks in my community get in first. " +
        "Use my code FOUNDING3 at signup to lock in your founding badge + free shipping for life 👇"
      ),
    },
  },
};

function CopyButton({ text, label = "Copy", testId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* noop */ }
  };
  return (
    <button
      type="button"
      onClick={copy}
      className="btn-secondary inline-flex items-center gap-1.5 text-xs px-3 py-1.5"
      data-testid={testId}
      aria-label={label}
    >
      {copied ? <Check size={13}/> : <Copy size={13}/>}
      {copied ? "Copied" : label}
    </button>
  );
}

function downloadQrPng(svgSelector, filename) {
  const svgEl = document.querySelector(svgSelector);
  if (!svgEl) return;
  const xml = new XMLSerializer().serializeToString(svgEl);
  const svg64 = btoa(unescape(encodeURIComponent(xml)));
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement("canvas");
    canvas.width = 512; canvas.height = 512;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, 512, 512);
    ctx.drawImage(img, 0, 0, 512, 512);
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = filename;
    a.click();
  };
  img.src = "data:image/svg+xml;base64," + svg64;
}

export default function AmbassadorKit() {
  const { slug } = useParams();
  const kit = KITS[String(slug || "").toLowerCase()];
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const fullPromoUrl = useMemo(() => (kit ? `${origin}${kit.promo_url_path}` : ""), [kit, origin]);
  const twitterShare = useMemo(() => {
    if (!kit) return "";
    const t = encodeURIComponent(kit.social_captions.short);
    const u = encodeURIComponent(fullPromoUrl);
    return `https://twitter.com/intent/tweet?text=${t}&url=${u}`;
  }, [kit, fullPromoUrl]);
  const facebookShare = useMemo(() => {
    if (!kit) return "";
    return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fullPromoUrl)}`;
  }, [kit, fullPromoUrl]);

  if (!kit) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-[var(--ff-cream)]" data-testid={`ambassador-kit-${kit.slug}`}>
      <Seo
        title={`${kit.name}'s founding link · The Farm To You`}
        description={kit.tagline}
        path={`/ambassadors/${kit.slug}`}
      />

      <div className="max-w-5xl mx-auto px-6 py-12 sm:py-16">
        <Link
          to="/ambassadors"
          className="inline-flex items-center gap-1 text-sm text-[var(--ff-text-muted)] hover:text-[var(--ff-primary)] transition-colors mb-8"
          data-testid="ambassador-kit-back"
        >
          <ChevronLeft size={14}/> All ambassadors
        </Link>

        {/* Hero */}
        <div className="grid md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-7">
            <p className="overline text-[var(--ff-primary)]">Ambassador press kit</p>
            <h1 className="font-display text-5xl sm:text-6xl mt-3 text-[var(--ff-primary)] leading-[1.02]" data-testid="ambassador-kit-name">
              {kit.name}
            </h1>
            <p className="mt-3 text-sm text-[var(--ff-text-muted)] font-mono">{kit.handle}</p>
            <p className="mt-6 text-lg text-[var(--ff-text-secondary)] leading-relaxed max-w-xl">
              {kit.tagline}
            </p>
          </div>

          {/* QR + code card */}
          <div className="md:col-span-5">
            <div className="card-flat p-6 sm:p-8 bg-white" data-testid="ambassador-kit-qr-card">
              <p className="overline text-[var(--ff-primary)]">Your promo code</p>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="font-display text-4xl text-[var(--ff-primary)] tabular-nums tracking-wider" data-testid="ambassador-kit-code">
                  {kit.promo_code}
                </span>
                <CopyButton text={kit.promo_code} label="Copy" testId="ambassador-kit-copy-code"/>
              </div>

              <div className="mt-6 flex items-center gap-4">
                <div className="bg-white border border-[var(--ff-border)] rounded-md p-2 flex-shrink-0" id={`amb-qr-wrap-${kit.slug}`}>
                  <QRCodeSVG
                    id={`amb-qr-${kit.slug}`}
                    value={fullPromoUrl}
                    size={112}
                    bgColor="#FFFFFF"
                    fgColor={kit.color_hex}
                    level="M"
                    includeMargin={false}
                    data-testid="ambassador-kit-qr"
                  />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-[var(--ff-text-muted)] uppercase tracking-wider font-semibold">Sign-up link</p>
                  <p className="mt-1 text-xs break-all font-mono text-[var(--ff-text-secondary)] leading-relaxed" data-testid="ambassador-kit-url">
                    {fullPromoUrl}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <CopyButton text={fullPromoUrl} label="Copy link" testId="ambassador-kit-copy-link"/>
                    <button
                      type="button"
                      onClick={() => downloadQrPng(`#amb-qr-${kit.slug}`, `farm-to-you-${kit.slug}-qr.png`)}
                      className="btn-secondary inline-flex items-center gap-1.5 text-xs px-3 py-1.5"
                      data-testid="ambassador-kit-download-qr"
                    >
                      <Download size={13}/> PNG
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Social captions */}
        <div className="mt-14 grid md:grid-cols-2 gap-6" data-testid="ambassador-kit-captions">
          <div className="card-flat p-6 bg-white">
            <div className="flex items-center justify-between mb-3">
              <p className="overline text-[var(--ff-primary)]">Short caption · Instagram, X</p>
              <CopyButton text={kit.social_captions.short} label="Copy" testId="ambassador-kit-copy-short"/>
            </div>
            <p className="text-sm text-[var(--ff-text-secondary)] leading-relaxed">{kit.social_captions.short}</p>
          </div>
          <div className="card-flat p-6 bg-white">
            <div className="flex items-center justify-between mb-3">
              <p className="overline text-[var(--ff-primary)]">Long caption · Facebook, LinkedIn</p>
              <CopyButton text={kit.social_captions.long} label="Copy" testId="ambassador-kit-copy-long"/>
            </div>
            <p className="text-sm text-[var(--ff-text-secondary)] leading-relaxed">{kit.social_captions.long}</p>
          </div>
        </div>

        {/* One-click share */}
        <div className="mt-10">
          <p className="overline text-[var(--ff-primary)]">Post in one tap</p>
          <div className="mt-4 flex flex-wrap gap-3" data-testid="ambassador-kit-share-buttons">
            <a
              href={twitterShare}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-black text-white rounded-full text-sm font-semibold hover:bg-black/85 transition-colors"
              data-testid="ambassador-kit-share-twitter"
            >
              <Twitter size={15}/> Share on X
            </a>
            <a
              href={facebookShare}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#1877F2] text-white rounded-full text-sm font-semibold hover:bg-[#1877F2]/85 transition-colors"
              data-testid="ambassador-kit-share-facebook"
            >
              <Facebook size={15}/> Share on Facebook
            </a>
            <button
              type="button"
              onClick={async () => {
                try {
                  await navigator.share?.({ title: `${kit.name} · The Farm To You`, text: kit.social_captions.short, url: fullPromoUrl });
                } catch { /* user cancelled */ }
              }}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[var(--ff-accent-kraft)] text-[var(--ff-primary)] rounded-full text-sm font-semibold hover:brightness-95 transition-all"
              data-testid="ambassador-kit-share-native"
            >
              <Instagram size={15}/> Share to Stories (mobile)
            </button>
            <CopyButton text={fullPromoUrl} label="Copy link" testId="ambassador-kit-share-copy"/>
          </div>
        </div>

        {/* How to use */}
        <div className="mt-14 card-flat p-6 sm:p-8 bg-[var(--ff-accent-kraft)]/25 border border-[var(--ff-accent-kraft)]" data-testid="ambassador-kit-how">
          <div className="flex items-start gap-4">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[var(--ff-primary)] text-white flex-shrink-0">
              <LinkIcon size={18}/>
            </span>
            <div>
              <p className="overline text-[var(--ff-primary)]">How it works</p>
              <h3 className="font-display text-xl text-[var(--ff-primary)] mt-1">Every signup with your code earns lifetime commission.</h3>
              <ul className="mt-4 space-y-2 text-sm text-[var(--ff-text-secondary)]">
                <li className="flex gap-2"><span className="text-[var(--ff-primary)] font-semibold">·</span> Anyone who signs up through your link auto-applies your promo code.</li>
                <li className="flex gap-2"><span className="text-[var(--ff-primary)] font-semibold">·</span> You earn 10% of every purchase they make for the life of their membership.</li>
                <li className="flex gap-2"><span className="text-[var(--ff-primary)] font-semibold">·</span> Track your earnings in the Ambassador Dashboard once your account is set up.</li>
              </ul>
            </div>
          </div>
          <Link to="/ambassadors" className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-[var(--ff-primary)] hover:underline" data-testid="ambassador-kit-dashboard-link">
            Ambassador dashboard <ChevronRight size={14}/>
          </Link>
        </div>
      </div>
    </div>
  );
}

// Directory / index page for internal-linking convenience
export function AmbassadorKitIndex() {
  return (
    <div className="min-h-screen bg-[var(--ff-cream)]" data-testid="ambassador-kit-index">
      <Seo title="Ambassador press kits · The Farm To You" description="Founding ambassador press kits" path="/ambassadors/kit" />
      <div className="max-w-5xl mx-auto px-6 py-16">
        <p className="overline text-[var(--ff-primary)]">Press kits</p>
        <h1 className="font-display text-5xl mt-3 text-[var(--ff-primary)]">Founding ambassadors.</h1>
        <p className="mt-4 text-[var(--ff-text-secondary)] max-w-2xl">
          Each ambassador has a personal press kit with their promo link, QR code, and ready-to-post captions.
        </p>
        <div className="mt-10 grid sm:grid-cols-2 gap-4">
          {Object.values(KITS).map(k => (
            <Link
              key={k.slug}
              to={`/ambassadors/${k.slug}`}
              className="card-flat p-6 bg-white hover:border-[var(--ff-primary)] transition-colors group"
              data-testid={`ambassador-kit-list-${k.slug}`}
            >
              <p className="overline text-[var(--ff-primary)]">{k.promo_code}</p>
              <h3 className="font-display text-2xl mt-2 text-[var(--ff-primary)] group-hover:underline">{k.name}</h3>
              <p className="text-xs text-[var(--ff-text-muted)] mt-1 font-mono">{k.handle}</p>
              <p className="text-sm text-[var(--ff-text-secondary)] mt-3">Open press kit <ChevronRight size={13} className="inline"/></p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
